import { api } from '@/lib/api';
import type {
  IApiResponse,
  IFabricConnection,
  IFabricCuratedObservabilityStatusResponse,
  IFabricEnvironment,
  IFabricIncidentSyncResult,
  IFabricIssueCurrentSyncResult,
  IFabricRawObservabilityStatusResponse,
  IFabricSyncRun,
  IFabricSyncState,
  IFabricWorkspace,
  ISyncStatusSummaryResponse,
} from '@skystone/shared';

export const fabricSyncApi = {
  getEnvironment: async (): Promise<IFabricEnvironment> => {
    const result = await api.get<IApiResponse<IFabricEnvironment>>('/fabric/environment');
    return result.data;
  },

  getConnection: async (): Promise<IFabricConnection> => {
    const result = await api.get<IApiResponse<IFabricConnection>>('/fabric/connection');
    return result.data;
  },

  verifyConnection: async (): Promise<IFabricConnection> => {
    const result = await api.post<IApiResponse<IFabricConnection>>('/fabric/connection/verify');
    return result.data;
  },

  disconnect: async (): Promise<IFabricConnection> => {
    const result = await api.post<IApiResponse<IFabricConnection>>('/fabric/connection/disconnect');
    return result.data;
  },

  getWorkspace: async (): Promise<IFabricWorkspace> => {
    const result = await api.get<IApiResponse<IFabricWorkspace>>('/fabric/workspace');
    return result.data;
  },

  syncIncidents: async (products?: string): Promise<IFabricIncidentSyncResult> => {
    const body = typeof products === 'string' ? { products } : undefined;
    const result = await api.post<IApiResponse<IFabricIncidentSyncResult>>('/fabric/sync/incidents', body);
    return result.data;
  },

  syncIssueCurrent: async (): Promise<IFabricIssueCurrentSyncResult> => {
    const result = await api.post<IApiResponse<IFabricIssueCurrentSyncResult>>('/fabric/sync/issue-current');
    return result.data;
  },

  getSyncRuns: async (filters?: { jobType?: string; entityType?: string; limit?: number }): Promise<IFabricSyncRun[]> => {
    const params = new URLSearchParams();
    if (filters?.jobType) {
      params.set('jobType', filters.jobType);
    }
    if (filters?.entityType) {
      params.set('entityType', filters.entityType);
    }
    if (typeof filters?.limit === 'number') {
      params.set('limit', String(filters.limit));
    }

    const query = params.toString();
    const endpoint = query ? `/fabric/sync/runs?${query}` : '/fabric/sync/runs';
    const result = await api.get<IApiResponse<IFabricSyncRun[]>>(endpoint);
    return result.data;
  },

  getSyncStates: async (filters?: { jobType?: string; entityType?: string; limit?: number }): Promise<IFabricSyncState[]> => {
    const params = new URLSearchParams();
    if (filters?.jobType) {
      params.set('jobType', filters.jobType);
    }
    if (filters?.entityType) {
      params.set('entityType', filters.entityType);
    }
    if (typeof filters?.limit === 'number') {
      params.set('limit', String(filters.limit));
    }

    const query = params.toString();
    const endpoint = query ? `/fabric/sync/states?${query}` : '/fabric/sync/states';
    const result = await api.get<IApiResponse<IFabricSyncState[]>>(endpoint);
    return result.data;
  },

  getRawSyncStatus: async (): Promise<IFabricRawObservabilityStatusResponse> => {
    const result = await api.get<IApiResponse<IFabricRawObservabilityStatusResponse>>('/fabric/sync/status/raw');
    return result.data;
  },

  getCuratedSyncStatus: async (): Promise<IFabricCuratedObservabilityStatusResponse> => {
    const result = await api.get<IApiResponse<IFabricCuratedObservabilityStatusResponse>>('/fabric/sync/status/curated');
    return result.data;
  },

  getSyncStatusSummary: async (): Promise<ISyncStatusSummaryResponse> => {
    const result = await api.get<IApiResponse<ISyncStatusSummaryResponse>>('/fabric/sync/status/summary');
    return result.data;
  },
};
